import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { Button } from "primereact/button";
import EzControlledInputWithFloatingLabel from "../../../../common/EzForm/EzControlledInput/WithFloatingLabel";
import EzControlledCheckBox from "../../../../common/EzForm/EzControlledCheckBox";
import EzControlledCheckBoxWithLabel from "../../../../common/EzForm/EzControlledCheckBox/WithLabel";
import Breadcrumb from "../../../../common/Breadcrumb";
import { APP_URLS } from "../../../../utils/constant";
import { getFormBaseValidationRule } from "../../../../utils/form/form-utils";
import { transformFormFieldValueMap, RoleBreadcrumb } from "./constant";
import RoleAccessRow from "./RoleAccessRow";
import "./style.css";

const CreateRoleComponent = (props) => {
  const navigate = useNavigate();
  const {
    isEdit,
    isLoading,
    formFieldValueMap,
    isRoleDetailSuccess,
    isRoleMenuAccessSuccess,
    createRole,
    updateRole,
    isRoleCreateSuccess,
    isUpdateRoleSuccess,
    allMenusList,
  } = props;

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: useMemo(
      () => transformFormFieldValueMap(formFieldValueMap),
      [formFieldValueMap]
    ),
  });

  const [accessData, setAccessData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllView, setSelectAllView] = useState(false);
  const [selectAllCreate, setSelectAllCreate] = useState(false);
  const [selectAllEdit, setSelectAllEdit] = useState(false);
  const [selectAllDelete, setSelectAllDelete] = useState(false);

  useEffect(() => {
    if (!isEdit && allMenusList?.length) {
      setAccessData(allMenusList);
    }
  }, [isEdit, allMenusList]);

  useEffect(() => {
    if (isEdit && isRoleDetailSuccess && formFieldValueMap) {
      reset(transformFormFieldValueMap(formFieldValueMap));
    }
  }, [isEdit, isRoleDetailSuccess, formFieldValueMap, reset]);

  useEffect(() => {
    if (isRoleMenuAccessSuccess && isEdit && allMenusList?.length && formFieldValueMap?.menuAccess) {
      const roleMenuAccess = formFieldValueMap.menuAccess;
      
      const mergePermissions = (menus, roleAccess) => {
        return menus.map((menu) => {
          const roleMenu = roleAccess.find((rm) => rm.menuId === menu.menuId);
          
          const mergedMenu = {
            ...menu,
            view: roleMenu?.view === true,
            createCrud: roleMenu?.createCrud === true,
            editCrud: roleMenu?.editCrud === true,
            canDelete: roleMenu?.canDelete === true,
          };

          // Recursively merge child menus
          if (menu.accessDTOList && menu.accessDTOList.length > 0) {
            mergedMenu.accessDTOList = mergePermissions(
              menu.accessDTOList,
              roleAccess
            );
          }

          return mergedMenu;
        });
      };

      const mergedData = mergePermissions(allMenusList, roleMenuAccess);
      setAccessData(mergedData);
    }
  }, [isRoleMenuAccessSuccess, formFieldValueMap?.menuAccess, allMenusList, isEdit, formFieldValueMap]);

  useEffect(() => {
    if (isRoleCreateSuccess || isUpdateRoleSuccess) {
      reset();
    }
  }, [isRoleCreateSuccess, isUpdateRoleSuccess, reset]);

  const handleChange = useCallback((menuId, field, checked) => {
    const updateMenuRecursive = (menus) => {
      return menus.map((menu) => {
        if (menu.menuId === menuId) {
          return { ...menu, [field]: checked };
        }
        if (menu.accessDTOList && menu.accessDTOList.length > 0) {
          return {
            ...menu,
            accessDTOList: updateMenuRecursive(menu.accessDTOList),
          };
        }
        return menu;
      });
    };

    setAccessData((prev) => updateMenuRecursive(prev));
  }, []);

  const extractPermissions = (menus, permissions = []) => {
    menus.forEach((menu) => {
      if (menu.view || menu.createCrud || menu.editCrud || menu.canDelete) {
        permissions.push({
          menuId: menu.menuId,
          view: menu.view,
          createCrud: menu.createCrud,
          editCrud: menu.editCrud,
          canDelete: menu.canDelete,
        });
      }

      if (menu.accessDTOList && menu.accessDTOList.length > 0) {
        extractPermissions(menu.accessDTOList, permissions);
      }
    });
    return permissions;
  };

  const onSubmit = (formData) => {
    const permissions = extractPermissions(accessData);

    const payload = {
      title: formData.title,
      menuAccess: permissions,
    };

    if (isEdit) {
      updateRole({ id: formFieldValueMap.id, data: payload });
    } else {
      createRole(payload);
    }
  };

  const handleSelectAll = useCallback((e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    setSelectAllView(checked);
    setSelectAllCreate(checked);
    setSelectAllEdit(checked);
    setSelectAllDelete(checked);

    setAccessData((prev) => {
      const cloned = JSON.parse(JSON.stringify(prev));
      
      const updateAll = (menus) => {
        menus.forEach((menu) => {
          menu.view = checked;
          menu.createCrud = checked;
          menu.editCrud = checked;
          menu.canDelete = checked;
          if (menu.accessDTOList && menu.accessDTOList.length > 0) {
            updateAll(menu.accessDTOList);
          }
        });
      };
      
      updateAll(cloned);
      return cloned;
    });
  }, []);

  const handleSelectAllView = useCallback((checked) => {
    setSelectAllView(checked);
    setAccessData((prev) => {
      const cloned = JSON.parse(JSON.stringify(prev));
      const updateView = (menus) => {
        menus.forEach((menu) => {
          menu.view = checked;
          if (menu.accessDTOList && menu.accessDTOList.length > 0) {
            updateView(menu.accessDTOList);
          }
        });
      };
      updateView(cloned);
      return cloned;
    });
  }, []);

  const handleSelectAllCreate = useCallback((checked) => {
    setSelectAllCreate(checked);
    setAccessData((prev) => {
      const cloned = JSON.parse(JSON.stringify(prev));
      const updateCreate = (menus) => {
        menus.forEach((menu) => {
          menu.createCrud = checked;
          if (menu.accessDTOList && menu.accessDTOList.length > 0) {
            updateCreate(menu.accessDTOList);
          }
        });
      };
      updateCreate(cloned);
      return cloned;
    });
  }, []);

  const handleSelectAllEdit = useCallback((checked) => {
    setSelectAllEdit(checked);
    setAccessData((prev) => {
      const cloned = JSON.parse(JSON.stringify(prev));
      const updateEdit = (menus) => {
        menus.forEach((menu) => {
          menu.editCrud = checked;
          if (menu.accessDTOList && menu.accessDTOList.length > 0) {
            updateEdit(menu.accessDTOList);
          }
        });
      };
      updateEdit(cloned);
      return cloned;
    });
  }, []);

  const handleSelectAllDelete = useCallback((checked) => {
    setSelectAllDelete(checked);
    setAccessData((prev) => {
      const cloned = JSON.parse(JSON.stringify(prev));
      const updateDelete = (menus) => {
        menus.forEach((menu) => {
          menu.canDelete = checked;
          if (menu.accessDTOList && menu.accessDTOList.length > 0) {
            updateDelete(menu.accessDTOList);
          }
        });
      };
      updateDelete(cloned);
      return cloned;
    });
  }, []);

  const getFormErrorMessage = (name) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const breadcrumbList = [
    ...RoleBreadcrumb.slice(0, 2),
    { path: "#", label: isEdit ? "Edit Role" : "Create Role" },
  ];

  return (
    <Container maxWidth="lg">
      <Breadcrumb list={breadcrumbList} />
      <div className="containerbox">
        <div className="make-relative sub-header">
          <div className="maintitlediv">
            <div className="ml-3">
              <h4 className="pagetitle">
                {isEdit ? "Update Role" : "Create Role"}
              </h4>
            </div>
            <div className="buttonarea">
              <Button
                tooltip="Back to Roles"
                tooltipOptions={{ position: "left" }}
                label="Back to Roles"
                className="p-button"
                style={{ borderRadius: "10px" }}
                onClick={() => navigate(APP_URLS.SYSTEM_ROLES.HOME)}
              />
            </div>
          </div>
        </div>

        <div className="form-demo" style={{ marginTop: "20px" }}>
          <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12} sm={6} md={4}>
                <EzControlledInputWithFloatingLabel
                  name="title"
                  control={control}
                  label="Role Title"
                  rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE_MESSAGE}
                  isError={errors["title"]}
                  errorMsg={getFormErrorMessage("title")}
                />
              </Grid>
            </Grid>

            <Grid>
              <div className="user-access-section">
                <h5 className="user-access-heading">Role Access</h5>
                <EzControlledCheckBoxWithLabel
                  name="selectAll"
                  value={selectAll}
                  control={control}
                  isError={errors["selectAll"]}
                  label="Select All"
                  labelClassName={"ml-2 mt-2"}
                  onChange={handleSelectAll}
                />
              </div>
              <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className="table-header-cell module-name-cell">
                        Module Name
                      </TableCell>
                      <TableCell className="table-header-cell permission-cell">
                        View
                        <div>
                          <EzControlledCheckBox
                            name="selectAllView"
                            control={control}
                            value={selectAllView}
                            onChange={(e) => handleSelectAllView(e.checked)}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="table-header-cell permission-cell">
                        Create
                        <div>
                          <EzControlledCheckBox
                            name="selectAllCreate"
                            control={control}
                            value={selectAllCreate}
                            onChange={(e) => handleSelectAllCreate(e.checked)}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="table-header-cell permission-cell">
                        Edit
                        <div>
                          <EzControlledCheckBox
                            name="selectAllEdit"
                            control={control}
                            value={selectAllEdit}
                            onChange={(e) => handleSelectAllEdit(e.checked)}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="table-header-cell delete-permission-cell">
                        Delete
                        <div>
                          <EzControlledCheckBox
                            name="selectAllDelete"
                            control={control}
                            value={selectAllDelete}
                            onChange={(e) => handleSelectAllDelete(e.checked)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accessData?.map((module) => (
                      <RoleAccessRow
                        key={module?.menuId}
                        module={module}
                        handleChange={handleChange}
                        control={control}
                        errors={errors}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid container spacing={2} mt={3} justifyContent="flex-end">
              <Grid item>
                <Button
                  type="button"
                  label="Cancel"
                  className="p-button-secondary"
                  onClick={() => navigate(APP_URLS.SYSTEM_ROLES.HOME)}
                />
              </Grid>
              <Grid item>
                <Button
                  type="submit"
                  label={isEdit ? "Update" : "Create"}
                  className="p-button-primary"
                  disabled={isLoading}
                />
              </Grid>
            </Grid>
          </form>
        </div>
      </div>
    </Container>
  );
};

export default CreateRoleComponent;
